import React, { useState } from 'react';
import config from '../config/environment';

const JoinRoom = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    roomId: '',
    playerName: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear room info when room ID changes
    if (name === 'roomId') {
      setRoomInfo(null);
      setError('');
    }
  };

  const checkRoom = async () => {
    if (!formData.roomId.trim()) {
      setError('Please enter a room ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.getApiUrl()}/api/room/${formData.roomId}/info`);
      const result = await response.json();

      if (result.success) {
        setRoomInfo(result.roomData);
      } else {
        setError(result.error || 'Room not found');
        setRoomInfo(null);
      }
    } catch (err) {
      setError('Failed to check room');
      setRoomInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = async () => {
    if (!formData.playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.getApiUrl()}/api/room/${formData.roomId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerName: formData.playerName,
          password: formData.password
        })
      });

      const result = await response.json();

      if (result.success) {
        onNavigate('game', {
          roomId: formData.roomId,
          playerName: formData.playerName,
          isCreator: false
        });
      } else {
        setError(result.error || 'Failed to join room');
      }
    } catch (err) {
      setError(err.message || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeControl = (timeControl) => {
    if (!timeControl) return 'Unlimited';
    if (typeof timeControl === 'string') return timeControl;
    if (timeControl.type === 'custom') return `${timeControl.time}s`;
    return timeControl.label || 'Unlimited';
  };

  const getGameStatus = (status) => {
    switch (status) {
      case 'waiting':
        return '⏳ Waiting for players';
      case 'playing':
        return '🎮 Game in progress';
      case 'finished':
        return '✅ Game finished';
      default:
        return status;
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Join Caro Room</h2>
        
        <div className="form-container">
          <div className="form-group">
            <label htmlFor="roomId">Room ID</label>
            <div className="input-with-button">
              <input
                type="text"
                id="roomId"
                name="roomId"
                value={formData.roomId}
                onChange={handleInputChange}
                placeholder="Enter room ID"
                required
              />
              <button 
                type="button" 
                onClick={checkRoom}
                className="secondary-button"
                disabled={loading || !formData.roomId.trim()}
              >
                {loading ? 'Checking...' : 'Check'}
              </button>
            </div>
          </div>

          {roomInfo && (
            <div className="room-info">
              <h3>Room Information</h3>
              <div className="room-details">
                <div className="detail-row">
                  <span className="label">Creator:</span>
                  <span className="value">{roomInfo.creator}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Board Size:</span>
                  <span className="value">{roomInfo.boardSize}×{roomInfo.boardSize}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Win Condition:</span>
                  <span className="value">{roomInfo.winCondition} in a row</span>
                </div>
                <div className="detail-row">
                  <span className="label">Time Control:</span>
                  <span className="value">{formatTimeControl(roomInfo.timeControl)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className="value">{getGameStatus(roomInfo.status)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Players:</span>
                  <span className="value">
                    {Object.keys(roomInfo.players || {}).length}/2
                  </span>
                </div>
                {roomInfo.isPrivate && (
                  <div className="detail-row">
                    <span className="label">Room Type:</span>
                    <span className="value">🔒 Private</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {roomInfo && (
            <>
              <div className="form-group">
                <label htmlFor="playerName">Your Name</label>
                <input
                  type="text"
                  id="playerName"
                  name="playerName"
                  value={formData.playerName}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  maxLength={20}
                  required
                />
              </div>

              {roomInfo.isPrivate && (
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter room password"
                  />
                </div>
              )}
            </>
          )}

          {error && <div className="error">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => onNavigate('home')}
            >
              Back
            </button>
            {roomInfo && (
              <button
                type="button"
                className="primary-button"
                onClick={joinRoom}
                disabled={loading || !formData.playerName.trim()}
              >
                {loading ? 'Joining...' : 'Join Room'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinRoom;
